import { useMemo, useState } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography, CircularProgress, Alert } from '@mui/material';
import { CheckCircle as CheckCircleIcon, Warning as WarningIcon } from '@mui/icons-material';
import { fetchWithAuth } from '../../api/backendApi';
import { useIncident } from '../../contexts/IncidentContext';

const RESOLVE_AUTHORIZED_ROLES = ['admin', 'superadmin', 'tenant_admin', 'overseer'];

const IncidentResolveButton = ({ incident, userRoles, theme }) => {
  const { clearForcedIncident } = useIncident();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resolveNote, setResolveNote] = useState('');
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState(null);

  const idempotencyKey = useMemo(() => {
    // Prevent duplicate resolve posts if user double-clicks
    if (!incident?.incidentId) return null;
    return `resolve_${incident.incidentId}`;
  }, [incident?.incidentId]);

  // Check if user has permission to resolve
  const canResolve = RESOLVE_AUTHORIZED_ROLES.some(role => userRoles.includes(role));

  if (!canResolve) {
    return null;
  }

  const handleResolveClick = () => {
    setConfirmOpen(true);
    setError(null);
  };

  const handleConfirmResolve = async () => {
    setResolving(true);
    setError(null);

    try {
      // Call backend to append RESOLVED event
      await fetchWithAuth(
        `/api/v2/tenants/${incident.tenantId}/incidents/${incident.incidentId}/events`,
        'POST',
        {
          type: 'RESOLVED',
          payload: {
            note: resolveNote || 'Incident resolved'
          },
          idempotencyKey
        }
      );

      console.log('[INCIDENT] Incident resolved:', incident.incidentId);
      
      // Close dialog - modal will close automatically when incident status updates
      setConfirmOpen(false);
      setResolveNote('');

      // If the incident was forced-open (e.g. from a flow result), clear it so the modal can close
      clearForcedIncident(incident.incidentId);
    } catch (err) {
      console.error('[INCIDENT] Failed to resolve incident:', err);
      setError(err.response?.data?.error || err.message || 'Failed to resolve incident');
    } finally {
      setResolving(false);
    }
  };

  const handleCancel = () => {
    setConfirmOpen(false);
    setResolveNote('');
    setError(null);
  };

  return (
    <>
      <Button
        variant="contained"
        color="success"
        size="large"
        startIcon={<CheckCircleIcon />}
        onClick={handleResolveClick}
        sx={{
          px: 4,
          py: 1.5,
          fontWeight: 600
        }}
      >
        Resolve Incident
      </Button>

      {/* Confirmation Dialog */}
      <Dialog 
        open={confirmOpen} 
        onClose={resolving ? undefined : handleCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Resolve Incident
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to resolve this incident?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            This will mark the incident as resolved and close all active panels. This action cannot be undone.
          </Typography>
          
          <TextField
            label="Resolution Note (Optional)"
            multiline
            rows={3}
            fullWidth
            value={resolveNote}
            onChange={(e) => setResolveNote(e.target.value)}
            placeholder="Enter details about the resolution..."
            disabled={resolving}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleCancel} 
            disabled={resolving}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmResolve} 
            variant="contained" 
            color="success"
            disabled={resolving}
            startIcon={resolving ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            {resolving ? 'Resolving...' : 'Confirm Resolve'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IncidentResolveButton;
